/* eslint eslint-comments/no-use: off */

import {isLoggedIn} from '@github-ui/client-env'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useConfirm} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {type PinnedCommentMutationOptions, usePinnedComment} from '../../hooks/use-pinned-comment'
import type {OptimisticConnection} from '../../mutations/comment-connection-overlay'
import {useMinimizeCommentMutation} from '../../mutations/minimize-comment-mutation'
import {useUnminimizeCommentMutation} from '../../mutations/unminimize-comment-mutation'
import {useDeleteIssueCommentMutation} from '../../mutations/use-comment-mutations'
import type {CommentAuthorType} from '../ActivityHeader'
import {CommentHeader} from '../CommentHeader'
import {type HIDE_OPTIONS, toReportedContentClassifier} from './HideCommentActions'
import type {IssueCommentNode} from './issue-viewer-view-query-types'

type IssueCommentHeaderAuthor = NonNullable<IssueCommentNode['author']>
type IssueCommentHeaderNamedAuthor = Extract<
  IssueCommentHeaderAuthor,
  {readonly __typename: 'User' | 'EnterpriseUserAccount' | 'Mannequin'}
>
type IssueCommentHeaderRepository = NonNullable<IssueCommentNode['repository']>
type IssueCommentHeaderIssue = NonNullable<IssueCommentNode['issue']>
type IssueCommentHeaderViaApp = NonNullable<IssueCommentNode['viaApp']>
type IssueCommentHeaderSponsorship = NonNullable<IssueCommentNode['authorToRepoOwnerSponsorship']>
type IssueCommentHeaderLastUserContentEdit = NonNullable<IssueCommentNode['lastUserContentEdit']>
type IssueCommentHeaderEditor = NonNullable<IssueCommentHeaderLastUserContentEdit['editor']>
type IssueCommentHeaderPinnedBy = NonNullable<IssueCommentNode['pinnedBy']>

// RELAY-CLEANUP(IssueComment_issueComment): widens nullable fields, and adds the client-only
// `pending*` schema-extension fields (populated via commitLocalUpdate, not fetched from the server),
// so data from the still-Relay IssueComment_issueComment parent (spread in IssueComment.tsx, and
// passed down through both the IssueCommentEditor and IssueCommentViewer parents) is assignable.
// Nested objects (repository, issue, viaApp, authorToRepoOwnerSponsorship, lastUserContentEdit,
// pinnedBy) are narrowed to only the sub-fields this component selects: the operation-level
// `IssueCommentNode` type merges in extra sibling fields selected by OTHER fragments spread on the
// same node (e.g. IssueComment_issueComment's own `repository` selection), which the still-Relay
// parent's own fragment-scoped `$data` types don't carry.
// Remove when IssueComment_issueComment leaves Relay.
export type IssueCommentHeaderFragment = RelayCompat<
  Pick<
    IssueCommentNode,
    | 'id'
    | 'databaseId'
    | 'url'
    | 'createdAt'
    | 'body'
    | 'bodyHTML'
    | 'authorAssociation'
    | 'viewerCanUpdate'
    | 'viewerCanDelete'
    | 'viewerCanMinimize'
    | 'viewerCanUnminimize'
    | 'viewerCanReport'
    | 'viewerCanReportToMaintainer'
    | 'viewerCanBlockFromOrg'
    | 'viewerCanUnblockFromOrg'
    | 'viewerCanPin'
    | 'viewerCanUnpin'
    | 'isHidden'
    | 'minimizedReason'
    | 'showSpammyBadge'
    | 'createdViaEmail'
    | 'viewerDidAuthor'
    | 'viewerCanReadUserContentEdits'
    | 'lastEditedAt'
  > & {
    readonly author:
      | (Pick<IssueCommentHeaderAuthor, 'id' | 'login' | '__typename' | 'profileUrl'> & {
          readonly name?: IssueCommentHeaderNamedAuthor['name']
        })
      | null
    // `owner` is hand-shaped (not derived from IssueCommentHeaderRepository['owner']) because Relay
    // flattens the Organization|User interface to a single shape (no `__typename`) when every queried
    // field is common to both, while the graphql-codegen-derived operation type keeps the discriminated
    // union. The still-Relay parents' fragment-scoped `$data` always matches this flattened shape.
    readonly repository: Pick<IssueCommentHeaderRepository, 'id' | 'name' | 'isPrivate' | 'nameWithOwner'> & {
      readonly owner: {readonly id: string; readonly login: string; readonly url: string}
    }
    readonly issue: Pick<IssueCommentHeaderIssue, 'id' | 'number'> | null
    readonly viaApp: Pick<IssueCommentHeaderViaApp, 'name' | 'url'> | null
    readonly authorToRepoOwnerSponsorship: Pick<IssueCommentHeaderSponsorship, 'createdAt' | 'isActive'> | null
    readonly lastUserContentEdit: {
      readonly editor: Pick<IssueCommentHeaderEditor, 'login' | 'url'> | null
    } | null
    readonly pinnedBy: Pick<IssueCommentHeaderPinnedBy, 'login'> | null
    readonly pendingMinimizeReason?: string | null
    readonly pendingBlock?: boolean | null
    readonly pendingUnblock?: boolean | null
  }
>

export type IssueCommentHeaderProps = {
  anchorBaseUrl?: string
  comment: IssueCommentHeaderFragment
  commentAuthorLogin: string
  editComment: () => void
  onReplySelect: (quotedText?: string) => void
  isMinimized?: boolean
  onMinimize?: (value: boolean) => void
  navigate: (url: string) => void
  avatarUrl: string
  commentRef?: React.RefObject<HTMLDivElement | null>
  /** When set, comment writes are mirrored into the optimistic-connection overlay. */
  optimisticConnection?: OptimisticConnection
  commentSubjectAuthorLogin?: string
  hideActions?: boolean
  commentAuthorType?: CommentAuthorType
  hasDisabledActions?: boolean
  onEditHover?: () => void
}

export function IssueCommentHeader({
  anchorBaseUrl,
  comment: commentData,
  commentAuthorLogin,
  editComment,
  onReplySelect,
  isMinimized = false,
  onMinimize: onMinimized,
  navigate,
  avatarUrl,
  commentRef,
  optimisticConnection,
  commentSubjectAuthorLogin,
  hideActions = false,
  commentAuthorType,
  hasDisabledActions = false,
  onEditHover,
}: IssueCommentHeaderProps) {
  const confirm = useConfirm()
  const {addToast} = useToastContext()
  const issueId = commentData.issue?.id
  const commentDataId = commentData.id
  const minimizeComment = useMinimizeCommentMutation()
  const unminimizeComment = useUnminimizeCommentMutation()

  const deleteIssueComment = useDeleteIssueCommentMutation({
    connection: optimisticConnection ?? {
      ownerId: issueId ?? commentDataId,
      connectionPath: ['timelineItems'],
    },
  })
  const pinnedCommentOptions = useMemo<PinnedCommentMutationOptions>(
    () => ({
      issueId: issueId ?? null,
      nameWithOwner: commentData.repository.nameWithOwner,
      comment: {
        id: commentData.id,
        shortBodyHTML: commentData.bodyHTML,
        createdAt: commentData.createdAt,
        url: commentData.url,
        author: commentData.author
          ? {
              __typename: commentData.author.__typename,
              id: commentData.author.id,
              login: commentData.author.login,
              avatarUrl,
              profileUrl: commentData.author.profileUrl ?? null,
            }
          : null,
      },
    }),
    [
      avatarUrl,
      commentData.author,
      commentData.bodyHTML,
      commentData.createdAt,
      commentData.id,
      commentData.repository.nameWithOwner,
      commentData.url,
      issueId,
    ],
  )
  const {pinComment, unpinComment} = usePinnedComment(pinnedCommentOptions)

  const hideComment = useCallback(
    async (reason: HIDE_OPTIONS) => {
      if (!commentDataId) return
      minimizeComment({
        input: {
          id: commentDataId,
          reason: toReportedContentClassifier(reason),
          owner: commentData.repository.owner.login,
          repo: commentData.repository.name,
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: 'Failed to hide comment',
          })
        },
      })
    },
    [addToast, commentData.repository.name, commentData.repository.owner.login, commentDataId, minimizeComment],
  )

  const unhideComment = useCallback(async () => {
    if (!commentDataId) return
    unminimizeComment({
      input: {
        id: commentDataId,
        owner: commentData.repository.owner.login,
        repo: commentData.repository.name,
      },
      onError: () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: 'Failed to unhide comment',
        })
      },
    })
  }, [addToast, commentData.repository.name, commentData.repository.owner.login, commentDataId, unminimizeComment])

  const deleteComment = useCallback(async () => {
    if (!commentDataId) return
    const performDelete = await confirm({
      title: LABELS.confirmations.deleteCommentTitle,
      content: LABELS.confirmations.deleteCommentContent,
      confirmButtonContent: LABELS.confirmations.deleteCommentConfirmButtonContent,
      confirmButtonType: 'danger',
    })

    if (!performDelete) return

    const onError = () => {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: ERRORS.couldNotDeleteComment,
      })
    }

    if (commentData.databaseId == null || !issueId) {
      onError()
    } else {
      deleteIssueComment({
        route: {
          owner: commentData.repository.owner.login,
          repo: commentData.repository.name,
          number: commentData.issue.number,
          databaseId: commentData.databaseId,
        },
        nodeId: commentDataId,
        onError,
      })
    }
  }, [confirm, commentData, commentDataId, deleteIssueComment, issueId, addToast])

  const url = anchorBaseUrl ? `${anchorBaseUrl}#issuecomment-${commentData.databaseId}` : commentData.url

  const viewerCanPin = isLoggedIn() && commentData.viewerCanPin
  const viewerCanUnpin = isLoggedIn() && commentData.viewerCanUnpin

  return (
    <CommentHeader
      comment={{
        ...commentData,
        referenceText: `#${commentData.issue?.number ?? '?'}`,
        url,
        viewerCanSeeMinimizeButton: true,
        viewerCanSeeUnminimizeButton: true,
        viewerCanUnminimize: commentData.viewerCanUnminimize ?? false,
        viaApp: commentData.viaApp || undefined,
        viewerCanPin,
        viewerCanUnpin,
      }}
      editComment={editComment}
      onReplySelect={onReplySelect}
      isMinimized={isMinimized}
      onMinimize={onMinimized}
      commentAuthorLogin={commentAuthorLogin}
      commentAuthorProfileName={commentData.author?.name}
      navigate={navigate}
      avatarUrl={avatarUrl}
      hideComment={hideComment}
      unhideComment={unhideComment}
      deleteComment={deleteComment}
      commentRef={commentRef}
      viewerDidAuthor={commentData.viewerDidAuthor}
      commentSubjectAuthorLogin={commentSubjectAuthorLogin}
      commentSubjectType="issue"
      hideActions={hideActions}
      isDisabled={hasDisabledActions}
      commentAuthorType={commentAuthorType}
      id={`issuecomment-${commentData.databaseId}`}
      pinComment={pinComment}
      unpinComment={unpinComment}
      pinnedByLogin={commentData?.pinnedBy?.login}
      onEditHover={onEditHover}
    />
  )
}
